/**
 * CycleSetupWizard — guided 3-step setup for cycle creators.
 *
 * 1. Configure the cycle (workflow, tracks, defaults)
 * 2. Configure Catchball (participants, working tier) — when workflow enables Catchball
 * 3. Kick off — compose and send the first request (or finish for non-Catchball cycles)
 *
 * Canon: Build Contract §1D — role-contextual default view for cycle owners during setup.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Rocket,
  Layers,
  ArrowUpDown,
  Settings2,
} from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import { WaypointTrail } from '@/components/ui/WaypointTrail';
import { WorkflowPresetSummary } from '@/components/prioritization/cycle-config/WorkflowPresetSummary';
import { CycleSessionDefaultsSection } from '@/components/prioritization/cycle-config/CycleSessionDefaultsSection';
import { TrackIndex } from '@/components/prioritization/TrackIndex';
import { CatchballParticipantConfig } from '@/components/prioritization/catchball/CatchballParticipantConfig';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useWorkflowStore } from '@/stores/prioritization/workflowStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useCatchballLoopStore } from '@/stores/prioritization/catchballLoopStore';
import { useUserCycleRoleContext } from '@/hooks/prioritization/useUserCycleRoleContext';
import { canSync } from '@/lib/permissionResolver';
import {
  getSetupSteps,
  isCycleSetupComplete,
  mergeSetupKickoffMetadata,
  parseSetupStepParam,
  setupStepIndex,
  setupStepNumber,
  type CycleSetupStepId,
} from '@/lib/cycleSetupPhase';
import type { DegapCycle } from '@/types/prioritization/degapCycle';
import { CycleStrategicContextSection } from '@/components/prioritization/cycle-setup/CycleStrategicContextSection';
import {
  wizardBtnPrimary,
  wizardBtnSecondary,
  wizardCard,
  wizardCardPad,
  wizardHeaderBar,
  wizardSectionDesc,
  wizardSectionTitle,
  wizardShell,
} from '@/components/prioritization/cycle-setup/setupWizardTheme';

const RequestComposerPage = lazy(() => import('@/pages/prioritization/catchball/RequestComposerPage'));

const STEP_LABELS: Record<CycleSetupStepId, { title: string; subtitle: string; icon: typeof Settings2 }> = {
  configure: {
    title: 'Chart the terrain',
    subtitle: 'Confirm workflow, tracks, and session defaults before the expedition begins.',
    icon: Layers,
  },
  catchball: {
    title: 'Assemble the team',
    subtitle: 'Choose who receives the ball and who can participate below them.',
    icon: ArrowUpDown,
  },
  kickoff: {
    title: 'Launch the expedition',
    subtitle: 'Send the first request to your working tier — recipients and context carry over from earlier steps.',
    icon: Rocket,
  },
};

function StepIndicator({
  steps,
  current,
}: {
  steps: CycleSetupStepId[];
  current: CycleSetupStepId;
}) {
  const currentIdx = steps.indexOf(current);
  const waypoints = steps.map((step, idx) => ({
    label: STEP_LABELS[step].title,
    status: (idx < currentIdx ? 'complete' : idx === currentIdx ? 'active' : 'pending') as 'complete' | 'active' | 'pending',
    tooltip: STEP_LABELS[step].subtitle,
  }));
  return <WaypointTrail waypoints={waypoints} orientation="horizontal" />;
}

function ConfigureCycleStep({
  cycleId,
  cycleName,
}: {
  cycleId: string;
  cycleName: string;
}) {
  const cycle = useDegapProcessStore((s) => s.cycles.find((c) => c.id === cycleId));
  const [showDefaults, setShowDefaults] = useState(false);

  if (!cycle) return null;

  return (
    <div className="space-y-6">
      <div className={`${wizardCard} overflow-hidden`}>
        <WorkflowPresetSummary cycle={cycle} />
      </div>

      <CycleStrategicContextSection cycleId={cycleId} cycleName={cycleName} />

      <div className={`${wizardCard} overflow-hidden`}>
        <TrackIndex cycleId={cycleId} cycleName={cycleName} embedded />
      </div>

      <div className={wizardCard}>
        <button
          type="button"
          onClick={() => setShowDefaults((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
        >
          <div>
            <h3 className={wizardSectionTitle}>Session defaults</h3>
            <p className={wizardSectionDesc}>
              Optional overrides inherited by tracks and sessions.
            </p>
          </div>
          <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
            {showDefaults ? 'Hide' : 'Expand'}
          </span>
        </button>
        {showDefaults && (
          <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] px-5 pb-5">
            <CycleSessionDefaultsSection cycle={cycle} />
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigureCatchballStep({
  cycleId,
  workspaceId,
  orgScopeNodeId,
}: {
  cycleId: string;
  workspaceId: string;
  orgScopeNodeId: string | null | undefined;
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className={`${wizardSectionTitle} mb-1`}>Catchball participants</h3>
        <p className={`${wizardSectionDesc} mb-4`}>
          Choose how people are determined for this cycle, then pick who specifically receives the
          ball at each level.
        </p>
        <CatchballParticipantConfig
          cycleId={cycleId}
          workspaceId={workspaceId}
          orgScopeNodeId={orgScopeNodeId}
        />
      </div>
    </div>
  );
}

export function CycleSetupWizard() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const cycle = useDegapProcessStore((s) =>
    cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined,
  );
  const fetchCycleById = useDegapProcessStore((s) => s.fetchCycleById);
  const updateCycle = useDegapProcessStore((s) => s.updateCycle);
  const startCycle = useDegapProcessStore((s) => s.startCycle);
  const syncState = useDegapProcessStore((s) => s.syncState);

  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const loops = useCatchballLoopStore((s) => s.loops);
  const getEffectiveConfigForCycle = useWorkflowStore((s) => s.getEffectiveConfigForCycle);

  const workspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? cycle?.workspaceId ?? '';
  const cycleLoops = useMemo(
    () => (cycleId ? loops.filter((l) => l.cycleId === cycleId) : []),
    [loops, cycleId],
  );

  const cycleRoleContext = useUserCycleRoleContext({ cycleId, activeLoopId: null });
  const canAccess =
    cycleRoleContext.isCycleOwner ||
    cycleRoleContext.isAdmin ||
    (workspaceId ? canSync('process.edit', { kind: 'resource', id: workspaceId }) : false);

  const [catchballEnabled, setCatchballEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const hydrateRef = useRef<{ cycleId: string | null; attempted: boolean }>({
    cycleId: null,
    attempted: false,
  });

  useEffect(() => {
    if (!cycleId) return;
    void fetchTracksForCycle(cycleId);
    void fetchLoops(cycleId);
  }, [cycleId, fetchTracksForCycle, fetchLoops]);

  const hydrateCycle = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    setHydrateError(null);
    const result = await fetchCycleById(cycleId);
    if (!result.success) {
      setHydrateError(result.error ?? 'Could not load this cycle.');
    }
    setLoading(false);
  }, [cycleId, fetchCycleById]);

  useEffect(() => {
    if (!cycleId) return;

    if (hydrateRef.current.cycleId !== cycleId) {
      hydrateRef.current = { cycleId, attempted: false };
      setHydrateError(null);
    }

    if (cycle) {
      setLoading(false);
      setHydrateError(null);
      return;
    }

    if (hydrateRef.current.attempted) {
      setLoading(false);
      return;
    }

    hydrateRef.current.attempted = true;
    void hydrateCycle();
  }, [cycleId, cycle, hydrateCycle]);

  useEffect(() => {
    if (!cycle) return;
    let cancelled = false;
    void getEffectiveConfigForCycle(cycle).then((config) => {
      if (!cancelled) setCatchballEnabled(config.catchballEnabled);
    });
    return () => {
      cancelled = true;
    };
  }, [cycle, getEffectiveConfigForCycle]);

  const retryHydrate = useCallback(() => {
    if (!cycleId) return;
    hydrateRef.current = { cycleId, attempted: false };
    void hydrateCycle();
  }, [cycleId, hydrateCycle]);

  const steps = useMemo(() => getSetupSteps(catchballEnabled), [catchballEnabled]);
  const currentStep = parseSetupStepParam(searchParams.get('step'), catchballEnabled);
  const stepMeta = STEP_LABELS[currentStep];
  const stepNum = setupStepNumber(currentStep, catchballEnabled);
  const totalSteps = steps.length;

  const goToStep = useCallback(
    (step: CycleSetupStepId) => {
      setSearchParams({ step: String(setupStepIndex(step, catchballEnabled) + 1) }, { replace: true });
    },
    [catchballEnabled, setSearchParams],
  );

  /** False when the cycle could not be started — the reason is on screen. */
  const completeSetup = useCallback(async (): Promise<boolean> => {
    if (!cycleId || !cycle) return false;
    setFinishing(true);
    setStartError(null);
    try {
      await updateCycle(cycleId, {
        metadata: mergeSetupKickoffMetadata(cycle) as DegapCycle['metadata'],
      });
      if (cycle.status === 'draft') {
        // The same governed launch as the Cycle Designer (migration 881).
        const started = await startCycle(cycleId);
        if (!started.ok) {
          setStartError(started.error ?? 'The cycle could not be started.');
          return false;
        }
      }
      return true;
    } finally {
      setFinishing(false);
    }
  }, [cycleId, cycle, updateCycle, startCycle]);

  const handleKickoffComplete = useCallback(async () => {
    if (!(await completeSetup())) return;
    if (catchballEnabled && cycleId) {
      navigate(`/prioritization/cascade/dashboard/${cycleId}`, {
        replace: true,
        state: {
          catchballFlash: {
            tone: 'success',
            message: 'Cycle is live. Requests are in flight.',
          },
        },
      });
    } else if (cycleId) {
      navigate(`/prioritization/cycles/${cycleId}`, { replace: true });
    }
  }, [completeSetup, catchballEnabled, cycleId, navigate]);

  const handleNonCatchballFinish = useCallback(async () => {
    if (!(await completeSetup())) return;
    if (cycleId) {
      navigate(`/prioritization/cycles/${cycleId}`, { replace: true });
    }
  }, [completeSetup, cycleId, navigate]);

  if (!cycleId) {
    return <Navigate to="/prioritization/cycles" replace />;
  }

  if (!cycle && loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading cycle…
      </div>
    );
  }

  if (!cycle) {
    const message =
      hydrateError ??
      syncState.syncError ??
      'This cycle could not be loaded. Check your connection and workspace access, then try again.';
    return (
      <div className="p-8 text-center max-w-md mx-auto">
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-4">{message}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={retryHydrate}
            className="text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:text-primary-400 dark:hover:text-primary-300"
          >
            Retry
          </button>
          <Link to="/prioritization/cycles" className="text-sm text-[var(--text-ter)] hover:underline dark:text-[var(--text-ter)]">
            Back to cycles
          </Link>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return <Navigate to={`/prioritization/cycles/${cycleId}`} replace />;
  }

  if (isCycleSetupComplete(cycle, cycleLoops)) {
    return <Navigate to={`/prioritization/cycles/${cycleId}`} replace />;
  }

  const currentIdx = setupStepIndex(currentStep, catchballEnabled);
  const prevStep = currentIdx > 0 ? steps[currentIdx - 1] : null;
  const nextStep = currentIdx < steps.length - 1 ? steps[currentIdx + 1] : null;

  return (
    <div className={wizardShell}>
      <div className={wizardHeaderBar}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/prioritization/cycles"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
            >
              <ArrowLeft className="h-4 w-4" />
              All cycles
            </Link>
            <Link
              to={`/prioritization/cycles/${cycleId}?advanced=1`}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]"
            >
              Advanced configuration →
            </Link>
          </div>
          <PageHeader
            icon={<Settings2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
            iconBg="bg-primary-50 dark:bg-primary-950/40"
            title={`Set up ${cycle.name}`}
            subtitle={
              catchballEnabled
                ? 'Configure your cycle, set up Catchball, then send the first request.'
                : 'Configure your cycle, then open the workspace.'
            }
          />
          <StepIndicator steps={steps} current={currentStep} />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
            Step {stepNum} of {totalSteps}
          </p>
          <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-1">
            {stepMeta.title}
          </h2>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1">{stepMeta.subtitle}</p>
        </div>

        {currentStep === 'configure' && (
          <ConfigureCycleStep cycleId={cycleId} cycleName={cycle.name} />
        )}

        {currentStep === 'catchball' && catchballEnabled && (
          <ConfigureCatchballStep
            cycleId={cycleId}
            workspaceId={workspaceId}
            orgScopeNodeId={cycle.orgScopeNodeId}
          />
        )}

        {currentStep === 'kickoff' && startError && (
          <p
            role="alert"
            data-testid="cycle-start-error"
            className="mb-4 border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 text-sm text-[var(--text-pri)]"
          >
            The cycle was not started: {startError}
          </p>
        )}

        {currentStep === 'kickoff' && catchballEnabled && (
          <div className={`${wizardCard} overflow-hidden`}>
            <Suspense
              fallback={
                <div className="flex justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
                </div>
              }
            >
              <RequestComposerPage
                embedded
                cycleIdProp={cycleId}
                onKickoffComplete={() => void handleKickoffComplete()}
              />
            </Suspense>
          </div>
        )}

        {currentStep === 'kickoff' && !catchballEnabled && (
          <div className={`${wizardCardPad} text-center max-w-lg mx-auto`}>
            <div className="w-12 h-12 rounded-full bg-[var(--green-dim)] dark:bg-[var(--green)]/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-6 w-6 text-[var(--green)] dark:text-[var(--text-ter)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Ready to go
            </h3>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-2">
              Your cycle is configured. Open the full cycle workspace to run tracks and sessions.
            </p>
            <button
              type="button"
              onClick={() => void handleNonCatchballFinish()}
              disabled={finishing}
              className={`mt-6 ${wizardBtnPrimary}`}
            >
              {finishing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Starting…
                </>
              ) : (
                <>
                  Open cycle
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        )}

        {currentStep !== 'kickoff' && (
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-[var(--rule)] dark:border-[var(--rule)]">
            {prevStep ? (
              <button
                type="button"
                onClick={() => goToStep(prevStep)}
                className={wizardBtnSecondary}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : (
              <span />
            )}
            {nextStep && (
              <button
                type="button"
                onClick={() => goToStep(nextStep)}
                className={wizardBtnPrimary}
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CycleSetupWizard;
